import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { DashboardLayout } from "@/components/layout/DashboardLayout";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogTitle,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import {
  Calendar,
  Users,
  Scissors,
  UserCog,
  Clock,
  ArrowRight,
  CheckCircle,
  XCircle,
  ExternalLink,
  Copy,
  TrendingUp,
  User,
  Phone,
  Mail,
  Loader2,
  FileText,
  CheckCircle2,
} from "lucide-react";
import OnboardingTour from "@/components/OnboardingTour";
import PaymentActions from "@/components/PaymentActions";

const formatDateKey = (date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const getStatusLabel = (status) => {
  const labels = {
    booked: "Pending",
    pending: "Pending",
    completed: "Completed",
    cancelled: "Cancelled",
    no_show: "No Show",
  };
  return labels[status] || status;
};

const Dashboard = () => {
  const { api, business, user } = useAuth();
  const [stats, setStats] = useState(null);
  const [todayAppointments, setTodayAppointments] = useState([]);
  const [staff, setStaff] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedAppointment, setSelectedAppointment] = useState(null);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [generatingInvoice, setGeneratingInvoice] = useState(false);

  useEffect(() => {
    if (business?.id && user?.role) {
      fetchDashboardData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [business, user?.role]);

  const fetchDashboardData = async () => {
    try {
      const [statsRes, appointmentsRes, staffRes] = await Promise.all([
        api.get(`/businesses/${business.id}/stats`),
        api.get(`/businesses/${business.id}/appointments`, {
          params: { date: formatDateKey(new Date()) },
        }),
        user?.role === "owner"
          ? api.get(`/businesses/${business.id}/staff`)
          : Promise.resolve({ data: [] }),
      ]);
      setStats(statsRes.data);
      setTodayAppointments(appointmentsRes.data);
      setStaff(staffRes.data);
    } catch (error) {
      console.error("Failed to fetch dashboard data:", error);
      toast.error("Failed to load dashboard data");
    } finally {
      setLoading(false);
    }
  };

  const copyBookingLink = () => {
    const link = `${window.location.origin}/book/${business.slug}`;
    navigator.clipboard.writeText(link);
    toast.success("Booking link copied!");
  };

  const openAppointmentDetail = (appointment) => {
    setSelectedAppointment(appointment);
    setDetailsOpen(true);
  };

  const getStatusBadge = (status) => {
    const styles = {
      booked: "bg-amber-50 text-amber-700 border border-amber-200",
      pending: "bg-amber-50 text-amber-700 border border-amber-200",
      completed: "bg-green-50 text-green-700 border border-green-200",
      cancelled: "bg-red-50 text-red-700 border border-red-200",
      no_show: "bg-rose-50 text-rose-700 border border-rose-200",
    };
    return `inline-flex items-center px-2.5 py-1 rounded-lg text-xs font-semibold ${styles[status] || styles.pending}`;
  };

  const handleStatusAndClose = async (appointmentId, status) => {
    const labels = {
      completed: "Appointment marked as Complete",
      no_show: "Marked as No Show",
      cancelled: "Appointment Cancelled",
    };
    try {
      await api.put(
        `/businesses/${business.id}/appointments/${appointmentId}`,
        { status },
      );
      toast.success(labels[status] || `Appointment ${status}`);
      setDetailsOpen(false);
      fetchDashboardData();
    } catch (error) {
      console.error("Failed to update appointment:", error);
      toast.error("Failed to update appointment");
    }
  };

  const updateAppointmentStatus = async (appointmentId, status) => {
    try {
      await api.put(
        `/businesses/${business.id}/appointments/${appointmentId}`,
        { status },
      );
      toast.success(`Appointment ${status}`);
      fetchDashboardData();
      if (selectedAppointment?.id === appointmentId) {
        setSelectedAppointment((prev) => ({ ...prev, status }));
      }
    } catch (error) {
      console.error("Failed to update appointment:", error);
      toast.error("Failed to update appointment");
    }
  };

  const generateInvoice = async (appointmentId) => {
    setGeneratingInvoice(true);
    try {
      const response = await api.post(
        `/businesses/${business.id}/invoices`,
        { appointment_id: appointmentId, tax_rate: 0 },
        { responseType: "blob" },
      );
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `invoice-${appointmentId.slice(0, 8)}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Invoice downloaded");
    } catch (error) {
      console.error("Failed to generate invoice:", error);
      toast.error("Failed to generate invoice");
    } finally {
      setGeneratingInvoice(false);
    }
  };

  // ── KPI card definitions ─────────────────────────────  ───────
  const statCards = [
    {
      label: "Today's Appointments",
      value: stats?.today_total_appointments || 0,
      icon: Clock,
      iconBg: "bg-purple-100",
      iconColor: "text-purple-600",
      valueColor: "text-purple-700",
      cardBg: "bg-white",
      border: "border-gray-100",
    },
    {
      label: "Completed Today",
      value: stats?.today_completed_appointments || 0,
      icon: CheckCircle,
      iconBg: "bg-green-100",
      iconColor: "text-green-600",
      valueColor: "text-green-700",
      cardBg: "bg-white",
      border: "border-gray-100",
    },
    {
      label: "Cancellations Today",
      value: stats?.today_cancelled_appointments || 0,
      icon: Calendar,
      iconBg: "bg-red-100",
      iconColor: "text-red-500",
      valueColor: "text-red-600",
      cardBg: "bg-white",
      border: "border-red-100",
    },
    {
      label: "No Show Today",
      value: stats?.today_noshow_appointments || 0,
      icon: XCircle,
      iconBg: "bg-rose-100",
      iconColor: "text-rose-600",
      valueColor: "text-rose-600",
      cardBg: "bg-white",
      border: "border-gray-100",
    },
  ];

  // ── Quick links ─────────────────────────────────────────────
  const quickLinks = [
    {
      label: "Add Service",
      path: "/dashboard/services",
      icon: Scissors,
      iconBg: "bg-pink-100",
      iconColor: "text-pink-600",
      tourId: "qa-services",
    },
    {
      label: "Invite Staff",
      path: "/dashboard/staff",
      icon: UserCog,
      iconBg: "bg-orange-100",
      iconColor: "text-orange-600",
      tourId: "qa-staff",
    },
    {
      label: "View Calendar",
      path: "/dashboard/calendar",
      icon: Calendar,
      iconBg: "bg-blue-100",
      iconColor: "text-blue-600",
      tourId: "qa-calendar",
    },
    {
      label: "Manage Customers",
      path: "/dashboard/customers",
      icon: Users,
      iconBg: "bg-purple-100",
      iconColor: "text-purple-600",
      tourId: null,
    },
  ];

  // ── Top services ────────────────────────────────────────────
  const topServices =
    stats?.top_services ||
    (() => {
      const counts = {};
      todayAppointments.forEach((a) => {
        if (a.service_name)
          counts[a.service_name] = (counts[a.service_name] || 0) + 1;
      });
      return Object.entries(counts)
        .sort((a, b) => b[1] - a[1])
        .slice(0, 4)
        .map(([name, count]) => ({ name, count }));
    })();

  const maxServiceCount = topServices.length
    ? Math.max(...topServices.map((s) => s.count))
    : 1;

  // ── Staff utilization ───────────────────────────────────────
  const staffUtilization = (() => {
    const staffBookings = staff.map((staffMember) => ({
      id: staffMember.id,
      name: staffMember.name || staffMember.email?.split("@")[0] || "Staff",
      bookings: todayAppointments.filter(
        (appointment) => appointment.staff_id === staffMember.id,
      ).length,
    }));
    const maxBookings = Math.max(
      ...staffBookings.map((staffMember) => staffMember.bookings),
      1,
    );
    return staffBookings
      .sort((a, b) => b.bookings - a.bookings)
      .slice(0, 4)
      .map((staffMember) => ({
        ...staffMember,
        percent: Math.round((staffMember.bookings / maxBookings) * 100),
      }));
  })();

  // ── Helpers ─────────────────────────────────────────────────
  const statusBorderColor = (status) => {
    switch (status) {
      case "booked":
        return "border-l-amber-400";
      case "completed":
        return "border-l-green-400";
      case "cancelled":
        return "border-l-red-400";
      case "no_show":
        return "border-l-rose-400";
      case "pending":
        return "border-l-amber-400";
      default:
        return "border-l-gray-300";
    }
  };

  const StatusBadge = ({ status }) => {
    const map = {
      booked: {
        cls: "bg-amber-100 text-amber-800",
        icon: <Clock className="w-3 h-3 text-amber-600" />,
        label: "Pending",
      },
      completed: {
        cls: "bg-green-100 text-green-700",
        icon: <CheckCircle className="w-3 h-3 text-green-500" />,
        label: "Completed",
      },
      cancelled: {
        cls: "bg-red-100 text-red-800",
        icon: <XCircle className="w-3 h-3 text-red-500" />,
        label: "Cancelled",
      },
      pending: {
        cls: "bg-amber-100 text-amber-800",
        icon: <Clock className="w-3 h-3 text-amber-600" />,
        label: "Pending",
      },
      no_show: {
        cls: "bg-rose-100 text-rose-800",
        icon: <XCircle className="w-3 h-3 text-rose-600" />,
        label: "No Show",
      },
    };
    const s = map[status] || map["pending"];
    return (
      <span
        title={s.label}
        aria-label={s.label}
        className={`inline-flex items-center gap-1 px-1.5 sm:px-2 py-0.5 rounded-full text-xs font-semibold whitespace-nowrap ${s.cls}`}
      >
        {s.icon}
        <span className="hidden sm:inline">{s.label}</span>
      </span>
    );
  };

  const StaffAvatar = ({ name }) => {
    const initials = name
      ? name
          .split(" ")
          .map((n) => n[0])
          .join("")
          .slice(0, 2)
          .toUpperCase()
      : "?";
    return (
      <div className="w-7 h-7 sm:w-8 sm:h-8 rounded-full bg-purple-100 flex items-center justify-center flex-shrink-0">
        <span className="text-xs font-bold text-purple-700">{initials}</span>
      </div>
    );
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <div className="animate-pulse text-gray-600">
            Loading dashboard...
          </div>
        </div>
      </DashboardLayout>
    );
  }

  // ── Reusable card content blocks (shared between desktop right col and mobile grid) ──
  const TopServicesContent = ({ compact = false }) => (
    <>
      <div
        className={`flex items-center gap-1.5 ${compact ? "mb-2" : "mb-3 sm:mb-4"}`}
      >
        <TrendingUp
          className={`${compact ? "w-3.5 h-3.5" : "w-4 h-4 sm:w-5 sm:h-5"} text-purple-600`}
        />
        <h3
          className={`font-bold text-gray-900 ${compact ? "text-xs" : "text-sm sm:text-lg"}`}
        >
          {compact ? "Top Services" : "Top Services This Week"}
        </h3>
      </div>
      {topServices.length === 0 ? (
        <p className="text-xs text-gray-400 text-center py-2">No data yet</p>
      ) : (
        <div className={`space-y-${compact ? "2" : "2 sm:space-y-3"}`}>
          {topServices.slice(0, compact ? 3 : 4).map((service, idx) => (
            <div key={idx}>
              <div className="flex items-center justify-between mb-0.5 sm:mb-1">
                <span className="text-xs font-medium text-gray-700 truncate">
                  {service.name}
                </span>
                <span className="text-xs font-bold text-gray-900 ml-1 flex-shrink-0">
                  {service.count}
                </span>
              </div>
              <div className="w-full bg-gray-100 rounded-full h-1.5">
                <div
                  className="bg-purple-500 h-1.5 rounded-full transition-all duration-500"
                  style={{
                    width: `${Math.round((service.count / maxServiceCount) * 100)}%`,
                  }}
                />
              </div>
            </div>
          ))}
        </div>
      )}
    </>
  );

  const StaffUtilizationContent = ({ compact = false }) => (
    <>
      <div
        className={`flex items-center gap-1.5 ${compact ? "mb-2" : "mb-3 sm:mb-4"}`}
      >
        <UserCog
          className={`${compact ? "w-3.5 h-3.5" : "w-4 h-4 sm:w-5 sm:h-5"} text-purple-600`}
        />
        <h3
          className={`font-bold text-gray-900 ${compact ? "text-xs" : "text-sm sm:text-lg"}`}
        >
          {compact ? "Staff Load" : "Staff Utilization Today"}
        </h3>
      </div>
      {staffUtilization.length === 0 ? (
        <p className="text-xs text-gray-400 text-center py-2">
          No staff data yet
        </p>
      ) : (
        <div className={`space-y-${compact ? "2" : "3 sm:space-y-4"}`}>
          {staffUtilization.slice(0, compact ? 3 : 4).map((staff, idx) => (
            <div key={idx} className="flex items-center gap-2">
              <StaffAvatar name={staff.name} />
              <div className="flex-1 min-w-0">
                <div className="flex items-center justify-between mb-0.5">
                  <span className="text-xs font-semibold text-gray-800 truncate">
                    {staff.name}
                  </span>
                  <span className="text-xs text-gray-400 ml-1 flex-shrink-0">
                    {staff.bookings}
                    {compact ? "" : ` appt${staff.bookings !== 1 ? "s" : ""}`}
                  </span>
                </div>
                <div className="w-full bg-gray-100 rounded-full h-1.5">
                  <div
                    className="bg-purple-500 h-1.5 rounded-full transition-all duration-500"
                    style={{ width: `${staff.percent}%` }}
                  />
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </>
  );

  const QuickLinksContent = ({ compact = false }) => (
    <>
      <h3
        className={`font-bold text-gray-900 ${compact ? "text-xs mb-2" : "text-sm sm:text-lg mb-3 sm:mb-4"}`}
      >
        Quick Links
      </h3>
      <div className="space-y-0.5 sm:space-y-1">
        {quickLinks.map((action) => (
          <Link
            key={action.path}
            to={action.path}
            data-tour={action.tourId}
            className={`flex items-center gap-2 ${compact ? "p-1.5" : "p-2 sm:p-3"} rounded-xl hover:bg-gray-50 transition-all group`}
          >
            <div
              className={`${compact ? "w-7 h-7" : "w-8 h-8 sm:w-10 sm:h-10"} rounded-xl ${action.iconBg} flex items-center justify-center flex-shrink-0`}
            >
              <action.icon
                className={`${compact ? "w-3.5 h-3.5" : "w-4 h-4 sm:w-5 sm:h-5"} ${action.iconColor}`}
              />
            </div>
            <span
              className={`font-semibold text-gray-800 flex-1 ${compact ? "text-xs" : "text-xs sm:text-sm"} truncate`}
            >
              {action.label}
            </span>
            <ArrowRight
              className={`${compact ? "w-3 h-3" : "w-3.5 h-3.5 sm:w-4 sm:h-4"} text-gray-400 group-hover:translate-x-0.5 group-hover:text-gray-600 transition-all flex-shrink-0`}
            />
          </Link>
        ))}
      </div>
    </>
  );

  return (
    <DashboardLayout>
      <OnboardingTour />
      <div
        className="space-y-4 sm:space-y-6 min-w-0 overflow-x-hidden"
        data-testid="owner-dashboard"
      >
        {/* ── Header ─────────────────────────────────────────────── */}
        <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
          <div>
            {/* <h1 className="font-bold text-2xl md:text-3xl text-gray-900">
              Dashboard
            </h1> */}
            <h1 className="font-heading text-3xl font-extrabold">
              Your Salon, At a Glance
            </h1>
            <p className="text-muted-foreground">
              See today’s bookings, manage your team, and grow your business —
              all in one place.
            </p>
            {/* <p className="text-sm md:text-base text-gray-500 mt-1">
              Welcome back! Here's what's happening today.
            </p> */}
          </div>

          {/* Buttons — 2 columns side by side on mobile, flex row on sm+ */}
          <div
            className="grid grid-cols-2 sm:flex sm:flex-row gap-2 sm:gap-3 sm:items-center"
            data-tour="booking-links"
          >
            <Button
              variant="outline"
              onClick={copyBookingLink}
              className="justify-center bg-white border-2 border-gray-300 hover:bg-gray-50 shadow-sm text-xs sm:text-sm px-3 sm:px-4"
              data-testid="copy-booking-link"
            >
              <Copy className="w-3.5 h-3.5 sm:w-4 sm:h-4 mr-1.5 sm:mr-2 flex-shrink-0" />
              <span className="truncate">Copy Booking Link</span>
            </Button>
            <a
              href={`/book/${business?.slug}`}
              target="_blank"
              rel="noopener noreferrer"
            >
              <Button
                className="w-full bg-purple-600 hover:bg-purple-700 text-white shadow-lg shadow-purple-600/30 text-xs sm:text-sm px-3 sm:px-4"
                data-testid="view-booking-page"
              >
                <span className="truncate">View Booking Page</span>
                <ExternalLink className="w-3.5 h-3.5 sm:w-4 sm:h-4 ml-1.5 sm:ml-2 flex-shrink-0" />
              </Button>
            </a>
          </div>
        </div>

        {/* ── 4 KPI Cards ────────────────────────────────────────── */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-2 sm:gap-3">
          {statCards.map((stat) => (
            <div
              key={stat.label}
              className={`${stat.cardBg} rounded-xl shadow-sm hover:shadow-md transition-all duration-200 p-3 sm:p-4 border ${stat.border}`}
            >
              <div className="flex items-center gap-2 sm:gap-3 mb-2 sm:mb-3">
                <div
                  className={`w-8 h-8 sm:w-10 sm:h-10 rounded-full ${stat.iconBg} flex items-center justify-center flex-shrink-0`}
                >
                  <stat.icon
                    className={`w-4 h-4 sm:w-5 sm:h-5 ${stat.iconColor}`}
                  />
                </div>
                <span
                  className={`text-2xl sm:text-3xl font-bold ${stat.valueColor}`}
                >
                  {stat.value}
                </span>
              </div>
              <div className="border-t border-gray-100 mb-1.5 sm:mb-2" />
              <div className="text-xs text-gray-500 font-medium leading-snug">
                {stat.label}
              </div>
            </div>
          ))}
        </div>

        {/* ── Main layout ─────────────────────────────────────────── */}
        {/*
          Desktop/iPad (lg): 3-col grid — Schedule (2 cols) | Right column (1 col)
          Mobile: Schedule full width, then 2-col grid below for insights + quick links
        */}
        <div className="grid lg:grid-cols-3 gap-4 sm:gap-6 min-w-0">
          {/* TODAY'S SCHEDULE — full width on mobile, 2/3 on desktop */}
          <div className="lg:col-span-2 min-w-0">
            <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-4 sm:p-6 h-full overflow-hidden">
              <div className="flex items-center justify-between mb-3 sm:mb-5 gap-2">
                <h2 className="font-bold text-base sm:text-xl text-gray-900 truncate">
                  Today's Schedule
                </h2>
                <Link
                  to="/dashboard/appointments"
                  className="text-xs sm:text-sm text-purple-600 hover:text-purple-700 font-semibold hover:underline flex-shrink-0"
                >
                  View all
                </Link>
              </div>

              {todayAppointments.length === 0 ? (
                <div className="text-center py-10 bg-gray-50 rounded-lg">
                  <Clock className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                  <p className="text-gray-500 text-sm font-medium">
                    No appointments scheduled for today
                  </p>
                </div>
              ) : (
                <div className="space-y-1.5 sm:space-y-2 min-w-0">
                  {todayAppointments.slice(0, 6).map((appointment) => (
                    <div
                      key={appointment.id}
                      onClick={() => openAppointmentDetail(appointment)}
                      className={`flex items-center gap-2 sm:gap-4 py-2 px-2.5 sm:p-4 rounded-xl bg-gray-50 hover:bg-gray-100 transition-all border-l-4 cursor-pointer min-w-0 ${statusBorderColor(appointment.status)}`}
                      data-testid={`dashboard-appointment-${appointment.id}`}
                    >
                      {/* Time + Duration */}
                      <div className="text-center w-12 sm:w-16 flex-shrink-0">
                        <div className="font-bold text-gray-900 text-xs sm:text-sm leading-tight">
                          {appointment.time}
                        </div>
                        <div className="text-[10px] sm:text-xs text-gray-400">
                          {appointment.duration}m
                        </div>
                      </div>

                      {/* Client + Service */}
                      <div className="flex-1 min-w-0">
                        <div className="font-semibold text-gray-900 truncate text-xs sm:text-sm">
                          {appointment.customer_name}
                        </div>
                        <div className="text-xs text-gray-500 truncate">
                          {appointment.service_name}
                        </div>
                      </div>

                      {/* Staff tag — hidden on mobile */}
                      {appointment.staff_name && (
                        <span className="hidden sm:inline-flex items-center px-2.5 py-1 rounded-lg bg-white border border-gray-200 text-xs font-medium text-gray-600 whitespace-nowrap shadow-sm">
                          {appointment.staff_name}
                        </span>
                      )}
                      <div className="flex-shrink-0">
                        <StatusBadge status={appointment.status} />
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* RIGHT COLUMN — visible on lg+ only; hidden on mobile (mobile uses grid below) */}
          <div className="hidden lg:flex flex-col gap-5">
            <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-5">
              <TopServicesContent />
            </div>
            <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-5">
              <StaffUtilizationContent />
            </div>
            <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-5">
              <QuickLinksContent />
            </div>
          </div>
        </div>

        {/* ── Mobile + iPad bottom section (hidden on lg+) ─────────── */}
        {/*
          iPad (sm–lg): 3 equal columns side by side
          Mobile (< sm): 2-col grid — left col has Top Services + Staff Load stacked, right col has Quick Links
        */}
        <div className="lg:hidden">
          {/* iPad: 3 columns */}
          <div className="hidden sm:grid sm:grid-cols-3 gap-4">
            <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-4">
              <TopServicesContent />
            </div>
            <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-4">
              <StaffUtilizationContent />
            </div>
            <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-4">
              <QuickLinksContent />
            </div>
          </div>

          {/* Mobile: 2-col grid */}
          <div className="grid grid-cols-2 gap-3 sm:hidden">
            {/* Left col: Top Services + Staff Load stacked */}
            <div className="space-y-3">
              <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-3">
                <TopServicesContent compact />
              </div>
              <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-3">
                <StaffUtilizationContent compact />
              </div>
            </div>
            {/* Right col: Quick Links */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-3">
              <QuickLinksContent compact />
            </div>
          </div>
        </div>
      </div>

      {/* Appointment Detail Modal */}
      <Dialog open={detailsOpen} onOpenChange={setDetailsOpen}>
        <DialogContent className="p-0 gap-0 max-w-[95vw] sm:max-w-lg w-full flex flex-col max-h-[100dvh] sm:max-h-[90vh] sm:rounded-2xl overflow-hidden border-0 shadow-2xl">
          <DialogDescription className="sr-only">
            View and manage appointment information
          </DialogDescription>

          {selectedAppointment && (
            <>
              {/* Header */}
              <div className="px-6 pt-6 pb-4 border-b border-slate-100 bg-white shrink-0">
                <DialogTitle className="text-xl font-bold text-slate-900 tracking-tight">
                  Appointment Details
                </DialogTitle>
                <div className="flex items-center gap-2 mt-3">
                  <span className={getStatusBadge(selectedAppointment.status)}>
                    {getStatusLabel(selectedAppointment.status)}
                  </span>
                  <span className="text-xs text-slate-400 font-mono bg-slate-50 px-2 py-1 rounded">
                    #{selectedAppointment.id?.slice(0, 8)}
                  </span>
                </div>
              </div>

              {/* Scrollable body */}
              <div className="overflow-y-auto flex-1 px-6 py-5 space-y-4 bg-slate-50/50">
                {/* Customer card */}
                <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
                  <div className="flex items-center gap-2 mb-3">
                    <User className="w-4 h-4 text-slate-400" />
                    <p className="text-xs font-semibold uppercase tracking-wider text-slate-500">
                      Customer
                    </p>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-sky-400 to-sky-600 text-white flex items-center justify-center font-bold text-lg shrink-0 shadow-sm">
                      {selectedAppointment.customer_name?.[0]?.toUpperCase() ||
                        "?"}
                    </div>
                    <div className="min-w-0">
                      <p className="font-semibold text-slate-900">
                        {selectedAppointment.customer_name}
                      </p>
                      {selectedAppointment.customer_email && (
                        <p className="text-xs text-slate-500 truncate flex items-center gap-1">
                          <Mail className="w-3 h-3" />
                          {selectedAppointment.customer_email}
                        </p>
                      )}
                      {selectedAppointment.customer_phone && (
                        <p className="text-xs text-slate-500 flex items-center gap-1">
                          <Phone className="w-3 h-3" />
                          {selectedAppointment.customer_phone}
                        </p>
                      )}
                    </div>
                  </div>
                </div>

                {/* Service + Schedule */}
                <div className="grid grid-cols-2 gap-3">
                  <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
                    <p className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-2">
                      Service
                    </p>
                    <p className="font-semibold text-slate-900 text-sm leading-snug">
                      {selectedAppointment.service_name}
                    </p>
                    <div className="flex items-center gap-2 mt-2 text-xs text-slate-500">
                      <Clock className="w-3.5 h-3.5 shrink-0" />
                      <span>{selectedAppointment.duration} min</span>
                      <span className="font-semibold text-emerald-600">
                        $
                        {selectedAppointment.service_price?.toFixed(2) ?? "N/A"}
                      </span>
                    </div>
                  </div>
                  <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
                    <p className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-2">
                      Schedule
                    </p>
                    <p className="font-semibold text-slate-900 text-sm leading-snug">
                      {selectedAppointment.date
                        ? new Date(
                            selectedAppointment.date + "T00:00:00",
                          ).toLocaleDateString("en-US", {
                            month: "short",
                            day: "numeric",
                            year: "numeric",
                          })
                        : ""}
                    </p>
                    <div className="flex items-center gap-1.5 mt-2 text-xs text-slate-500">
                      <Clock className="w-3.5 h-3.5 shrink-0" />
                      <span className="font-medium">
                        {selectedAppointment.time} –{" "}
                        {selectedAppointment.end_time}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Customer notes */}
                {selectedAppointment.notes && (
                  <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
                    <p className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-2">
                      Customer Notes
                    </p>
                    <p className="text-sm text-slate-700">
                      {selectedAppointment.notes}
                    </p>
                  </div>
                )}

                {/* Payment Actions */}
                <PaymentActions
                  appointment={selectedAppointment}
                  api={api}
                  onUpdate={() => {
                    fetchDashboardData();
                    setDetailsOpen(false);
                  }}
                />
              </div>

              {/* Action footer */}
              <div className="shrink-0 px-6 py-4 border-t border-slate-100 bg-white">
                <div className="flex flex-col sm:flex-row gap-2">
                  {selectedAppointment.status === "booked" && (
                    <>
                      <Button
                        className="flex-1 h-11 bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-semibold rounded-xl shadow-sm"
                        onClick={() =>
                          handleStatusAndClose(
                            selectedAppointment.id,
                            "completed",
                          )
                        }
                        data-testid="dash-complete-btn"
                      >
                        <CheckCircle className="w-4 h-4 mr-2" />
                        Complete
                      </Button>
                      <Button
                        variant="outline"
                        className="flex-1 h-11 text-sm font-semibold rounded-xl border-slate-200"
                        onClick={() =>
                          handleStatusAndClose(
                            selectedAppointment.id,
                            "no_show",
                          )
                        }
                        data-testid="dash-noshow-btn"
                      >
                        <Clock className="w-4 h-4 mr-2" />
                        No Show
                      </Button>
                    </>
                  )}
                  {selectedAppointment.status === "pending" && (
                    <Button
                      className="flex-1 h-11 bg-sky-600 hover:bg-sky-700 text-white text-sm font-semibold rounded-xl shadow-sm"
                      onClick={() =>
                        updateAppointmentStatus(
                          selectedAppointment.id,
                          "booked",
                        )
                      }
                      data-testid="dash-confirm-btn"
                    >
                      <CheckCircle2 className="w-4 h-4 mr-2" />
                      Confirm Appointment
                    </Button>
                  )}
                  {selectedAppointment.status === "completed" && (
                    <Button
                      variant="outline"
                      className="flex-1 h-11 text-sm font-semibold rounded-xl border-slate-200"
                      onClick={() => generateInvoice(selectedAppointment.id)}
                      disabled={generatingInvoice}
                      data-testid="dash-invoice-btn"
                    >
                      {generatingInvoice ? (
                        <>
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                          Generating...
                        </>
                      ) : (
                        <>
                          <FileText className="w-4 h-4 mr-2" />
                          Generate Invoice
                        </>
                      )}
                    </Button>
                  )}
                  {selectedAppointment.status !== "cancelled" &&
                    selectedAppointment.status !== "completed" && (
                      <Button
                        variant="outline"
                        className="flex-1 h-11 text-sm font-semibold text-red-600 border-red-200 hover:bg-red-50 rounded-xl"
                        onClick={() =>
                          handleStatusAndClose(
                            selectedAppointment.id,
                            "cancelled",
                          )
                        }
                        data-testid="dash-cancel-btn"
                      >
                        <XCircle className="w-4 h-4 mr-2" />
                        Cancel
                      </Button>
                    )}
                </div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  );
};

export default Dashboard;
